import { z } from "zod";

/** Mirrors `SlotLine`: one service of an offered time. The name is null when the salon hides its stylists. */
export const slotLineSchema = z.object({
  serviceId: z.string(),
  staffId: z.string(),
  staffName: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
});

/** Mirrors `AvailableSlot`: one time a customer could come in, with who would do each service. */
export const availableSlotSchema = z.object({
  startsAt: z.string(),
  lines: z.array(slotLineSchema),
});

/** Mirrors `AvailabilityAlternatives`: the nearest offered starts either side of a day with none (FR-SA-066). */
export const availabilityAlternativesSchema = z.object({
  earlier: z.string().nullable(),
  later: z.string().nullable(),
});

/**
 * Mirrors `UnavailableReasons`: why a time of the day cannot be had (FR-SA-156, FR-SA-157). A reason
 * a newer server adds reads as `away`, the plainest "not available".
 */
export const unavailableReasonSchema = z
  .enum(["booked", "lunch", "past", "too_soon", "away"])
  .catch("away");

/** Mirrors `UnavailableTime`. */
export const unavailableTimeSchema = z.object({
  startsAt: z.string(),
  reason: unavailableReasonSchema,
});

/**
 * Mirrors `AvailableDay`. A shut day is an empty list, not a missing entry. `closed` tells a shut day
 * from a full one, and `alternatives` comes only on an empty day of the public answer (Phase 4
 * design D0b); older servers send neither. `unavailable` is the rest of the day's times, each with
 * its reason, so the whole day is shown (FR-SA-156, FR-SA-157); a shut day and an older server send none.
 */
export const availableDaySchema = z.object({
  date: z.string(),
  slots: z.array(availableSlotSchema),
  alternatives: availabilityAlternativesSchema.nullable().default(null),
  closed: z.boolean().default(false),
  unavailable: z
    .array(unavailableTimeSchema)
    .nullish()
    .transform((unavailable) => unavailable ?? []),
});

export const availabilitySchema = z.array(availableDaySchema);

export type SlotLine = z.infer<typeof slotLineSchema>;
export type AvailableSlot = z.infer<typeof availableSlotSchema>;
export type AvailableDay = z.infer<typeof availableDaySchema>;
export type UnavailableReason = z.infer<typeof unavailableReasonSchema>;
export type UnavailableTime = z.infer<typeof unavailableTimeSchema>;
export type AvailabilityAlternatives = z.infer<typeof availabilityAlternativesSchema>;
